import { ArrowRightOutlined } from "@ant-design/icons";
import { Collapse } from "antd";
import { useEffect, useRef, useState } from "react";
import { HelpFaqItem } from "../../types/helpCentre.types";
import HelpMarkdown from "./HelpMarkdown";

interface FaqListProps {
	items: HelpFaqItem[];
	/** `cards` for a topic's FAQ section, `plain` rows for the landing page's panel. */
	variant?: "cards" | "plain";
	/** An item to open and scroll to, such as `?faq=`'s; followed whenever it changes. */
	openKey?: string | null;
	emptyText?: string;
}

/** Questions that open to their markdown answers; several can be open at once. */
function FaqList({
	items,
	variant = "cards",
	openKey,
	emptyText = "No questions yet.",
}: Readonly<FaqListProps>) {
	const [openKeys, setOpenKeys] = useState<string[]>(() =>
		openKey ? [openKey] : []
	);
	const questions = useRef(new Map<string, HTMLElement>());

	// A passive effect: it runs after the Help Centre layout's scroll reset,
	// which is a layout effect, so that reset can't undo this scroll.
	useEffect(() => {
		if (!openKey) return;
		setOpenKeys((keys) => (keys.includes(openKey) ? keys : [...keys, openKey]));
		const question = questions.current.get(openKey);
		question?.scrollIntoView({ block: "center" });
		question
			?.closest<HTMLElement>(".ant-collapse-header")
			?.focus({ preventScroll: true });
	}, [openKey]);

	if (!items.length) return <p className="help-empty">{emptyText}</p>;

	return (
		<Collapse
			className={`help-faq help-faq--${variant}`}
			bordered={false}
			expandIconPosition="end"
			activeKey={openKeys}
			onChange={(keys) => setOpenKeys(([] as string[]).concat(keys))}
			items={items.map((item) => ({
				key: item.key,
				label: (
					<span
						className="help-faq__question"
						ref={(element) => {
							if (element) questions.current.set(item.key, element);
							else questions.current.delete(item.key);
						}}
					>
						{item.question}
					</span>
				),
				children: (
					<div className="help-faq__answer">
						<ArrowRightOutlined
							aria-hidden="true"
							className="help-faq__arrow"
						/>
						<HelpMarkdown markdown={item.answer} />
					</div>
				),
			}))}
		/>
	);
}

export default FaqList;
